import React, { useEffect, useState } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Radio, InputNumber,
  List, Tag, Popconfirm, App, Spin, Empty, Tabs, Upload,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined,
  DragOutlined, AppstoreOutlined, EyeOutlined, WarningOutlined, ThunderboltOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import api from '../api/client';
import MathRenderer from '../components/MathRenderer';

const DIFF_COLOUR = { foundation: '#389e0d', intermediate: '#0958d9', extension: '#c41d7f' };

function SortableStageCard({ stage, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.stage_id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <Card variant="outlined" style={{ borderColor: '#e8e8e8', marginBottom: 8 }} bodyStyle={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#ccc', fontSize: 16, flexShrink: 0 }}>
            <DragOutlined />
          </span>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#0a0a0a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {Math.round(stage.stage_order / 10)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a' }}>{stage.stage_name}</div>
            {stage.gate_description && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {stage.gate_description}
              </div>
            )}
            <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
              <Tag style={{ fontSize: 10, color: DIFF_COLOUR[stage.difficulty], borderColor: DIFF_COLOUR[stage.difficulty], background: 'transparent' }}>
                {stage.difficulty}
              </Tag>
              <span style={{ fontSize: 11, color: '#aaa' }}>{stage.effort_minutes} min</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit(stage)} />
            <Popconfirm title="Delete this stage?" onConfirm={() => onDelete(stage.stage_id)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function StageGateBuilder() {
  const { user } = useUser();
  const [templates, setTemplates]   = useState([]);
  const [systemTmpls, setSystemTmpls] = useState([]);
  const [selected, setSelected]     = useState(null);
  const [stages, setStages]         = useState([]);
  const [subjects, setSubjects]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [newTmplModal, setNewTmplModal]   = useState(false);
  const [newTmplTab, setNewTmplTab]       = useState('blank');
  const [moeLoading, setMoeLoading]       = useState(false);
  const [selectedMoe, setSelectedMoe]     = useState(null);
  const [stageModal, setStageModal]       = useState(false);
  const [editingStage, setEditingStage]   = useState(null);
  const [stageQuestions, setStageQuestions] = useState([]);
  const [aiGenerating, setAiGenerating]   = useState(false);
  const [imgUploading, setImgUploading]   = useState(false);
  const [aiCount, setAiCount]             = useState(3);
  const [previewIdx, setPreviewIdx]       = useState(null);
  const [saving, setSaving]               = useState(false);
  const [renameModal, setRenameModal]     = useState(false);
  const [renameForm]                      = Form.useForm();
  const [tmplForm]  = Form.useForm();
  const [stageForm] = Form.useForm();
  const [moeForm]   = Form.useForm();
  const { message } = App.useApp();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tmplRes, subRes] = await Promise.all([
        api.get('/api/gate/templates/mine'),
        api.get(`/api/users/${user.userId}/subjects`),
      ]);
      setTemplates(tmplRes.data);
      setSubjects(subRes.data);
    } catch { message.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const loadStages = async (templateId) => {
    setStagesLoading(true);
    try {
      const res = await api.get(`/api/gate/templates/${templateId}/stages`);
      setStages(res.data);
    } catch { message.error('Failed to load stages'); }
    finally { setStagesLoading(false); }
  };

  const selectTemplate = (tmpl) => { setSelected(tmpl); loadStages(tmpl.template_id); };

  const openNewTmplModal = async () => {
    setNewTmplTab('blank');
    setSelectedMoe(null);
    tmplForm.resetFields();
    moeForm.resetFields();
    setNewTmplModal(true);
    if (systemTmpls.length === 0) {
      setMoeLoading(true);
      try {
        const res = await api.get('/api/gate/templates/system');
        setSystemTmpls(res.data);
      } catch { message.error('Failed to load MOE templates'); }
      finally { setMoeLoading(false); }
    }
  };

  const handleCreateBlank = async (values) => {
    setSaving(true);
    try {
      await api.post('/api/gate/templates', values);
      message.success('Template created');
      setNewTmplModal(false);
      tmplForm.resetFields();
      load();
    } catch { message.error('Failed to create template'); }
    finally { setSaving(false); }
  };

  const handleCopyMoe = async (values) => {
    if (!selectedMoe) { message.warning('Select an MOE template first'); return; }
    try {
      const res = await api.post(`/api/gate/templates/${selectedMoe.template_id}/copy`, {
        title: values.title || `${selectedMoe.title} (My Copy)`,
        tutor_subject_id: values.tutor_subject_id,
      });
      message.success('Template copied — you can now edit it');
      setNewTmplModal(false);
      setSelectedMoe(null);
      moeForm.resetFields();
      await load();
      const fresh = (await api.get('/api/gate/templates/mine')).data;
      setTemplates(fresh);
      const copied = fresh.find(t => t.template_id === res.data.template_id);
      if (copied) selectTemplate(copied);
    } catch { message.error('Failed to copy template'); }
  };

  const handleTogglePreview = async (templateId) => {
    try {
      const res = await api.patch(`/api/gate/templates/${templateId}/preview`);
      setTemplates(ts => ts.map(t => t.template_id === templateId ? { ...t, is_preview: res.data.is_preview } : t));
      if (selected?.template_id === templateId) setSelected(s => ({ ...s, is_preview: res.data.is_preview }));
    } catch { message.error('Failed to update preview'); }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/api/gate/templates/${templateId}`);
      message.success('Template deleted');
      if (selected?.template_id === templateId) { setSelected(null); setStages([]); }
      load();
    } catch { message.error('Failed to delete template'); }
  };

  const handleRename = async (values) => {
    try {
      await api.patch(`/api/gate/templates/${selected.template_id}`, { title: values.title, tutor_subject_id: values.tutor_subject_id });
      setSelected(s => ({ ...s, title: values.title, tutor_subject_id: values.tutor_subject_id }));
      setTemplates(ts => ts.map(t => t.template_id === selected.template_id ? { ...t, title: values.title } : t));
      setRenameModal(false); renameForm.resetFields();
      message.success('Template updated');
    } catch { message.error('Failed to update'); }
  };

  const openStageModal = async (stage = null) => {
    setEditingStage(stage);
    if (stage) {
      stageForm.setFieldsValue(stage);
      // Load existing questions
      try {
        const res = await api.get(`/api/gate/stages/${stage.stage_id}/questions`);
        setStageQuestions(res.data.map(q => ({ question_text: q.question_text || '', image_url: q.image_url || null })));
      } catch { setStageQuestions([]); }
    } else {
      stageForm.setFieldsValue({ difficulty: 'intermediate', effort_minutes: 30 });
      setStageQuestions([]);
    }
    setStageModal(true);
  };

  const handleGenerateQuestions = async () => {
    const stageName = stageForm.getFieldValue('stage_name');
    const difficulty = stageForm.getFieldValue('difficulty') || 'intermediate';
    const subject = subjects.find(s => s.subject_id === selected?.tutor_subject_id);
    const subjectName = subject ? `${subject.subject_name} ${subject.level}` : 'General';
    const subjectLevel = subject?.level || '';

    if (!stageName) { message.warning('Enter a stage name first — it will be used as the topic.'); return; }

    setAiGenerating(true);
    try {
      const res = await api.post('/api/ai/generate', {
        type: 'questions_only',
        subject: subjectName,
        level: subjectLevel,
        topic: stageName,
        difficulty,
        count: aiCount,
      });
      // Parse "**Question N**" blocks into individual strings
      const raw = res.data.content || '';
      const blocks = raw.split(/\*\*Question\s+\d+\*\*/i).filter(b => b.trim());
      const parsed = blocks.map(b => b.replace(/^\s*\[\d+\s*marks?\]\s*/i, '').trim()).filter(Boolean);
      if (parsed.length) {
        setStageQuestions(prev => [...prev, ...parsed.map(text => ({ question_text: text, image_url: null }))]);
        message.success(`${parsed.length} question${parsed.length !== 1 ? 's' : ''} generated`);
      } else {
        // Fallback: add raw content as a single question
        setStageQuestions(prev => [...prev, { question_text: raw.trim(), image_url: null }]);
        message.success('Questions generated');
      }
    } catch { message.error('Generation failed'); }
    finally { setAiGenerating(false); }
  };

  // Upload an image of a question to S3, then add it as an image-based question.
  const handleUploadQuestionImage = async (file) => {
    if (!file.type?.startsWith('image/')) { message.error('Please choose an image file'); return false; }
    setImgUploading(true);
    try {
      const urlRes = await api.get('/api/materials/upload-url', {
        params: { fileName: `q_${Date.now()}_${file.name}`, fileType: file.type },
      });
      await fetch(urlRes.data.uploadURL, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const url = urlRes.data.uploadURL.split('?')[0];
      setStageQuestions(prev => [...prev, { question_text: '', image_url: url }]);
      message.success('Question image added');
    } catch { message.error('Image upload failed'); }
    finally { setImgUploading(false); }
    return false; // stop antd from auto-uploading
  };

  const handleSaveStage = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values, questions: stageQuestions.filter(q => q.question_text?.trim() || q.image_url) };
      if (editingStage) {
        await api.patch(`/api/gate/stages/${editingStage.stage_id}`, payload);
        message.success('Stage updated');
      } else {
        await api.post(`/api/gate/templates/${selected.template_id}/stages`, payload);
        message.success('Stage added');
      }
      setStageModal(false); stageForm.resetFields(); setEditingStage(null); setStageQuestions([]); setPreviewIdx(null);
      loadStages(selected.template_id);
    } catch { message.error('Failed to save stage'); }
    finally { setSaving(false); }
  };

  const handleDeleteStage = async (stageId) => {
    try { await api.delete(`/api/gate/stages/${stageId}`); message.success('Stage removed'); loadStages(selected.template_id); }
    catch { message.error('Failed to delete stage'); }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = stages.findIndex(s => s.stage_id === active.id);
    const newIdx = stages.findIndex(s => s.stage_id === over.id);
    const reordered = arrayMove(stages, oldIdx, newIdx);
    setStages(reordered);
    try { await api.patch(`/api/gate/templates/${selected.template_id}/stages/reorder`, { orderedStageIds: reordered.map(s => s.stage_id) }); }
    catch { message.error('Failed to save order'); loadStages(selected.template_id); }
  };

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h1>Roadmap Builder</h1>
        <p>Create and manage your Roadmap templates.</p>
      </div>

      <div style={{ display: 'flex', gap: 20, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* ── Left panel ── */}
        <div style={{ width: 280, flexShrink: 0, height: '100%', overflowY: 'auto' }}>
          <Button icon={<PlusOutlined />} size="small" type="primary" block
            style={{ background: '#0a0a0a', border: 'none', marginBottom: 12 }}
            onClick={openNewTmplModal}>
            New template
          </Button>

          {loading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : templates.length === 0
              ? <Empty description="No templates yet" style={{ padding: 40 }} imageStyle={{ height: 40 }} />
              : (
                <List dataSource={templates} renderItem={t => (
                  <List.Item style={{ padding: 0, marginBottom: 6 }}>
                    <div onClick={() => selectTemplate(t)} style={{
                      width: '100%', padding: '10px 14px', cursor: 'pointer',
                      border: `1px solid ${selected?.template_id === t.template_id ? '#0a0a0a' : '#e8e8e8'}`,
                      borderRadius: 8, background: selected?.template_id === t.template_id ? '#f5f5f5' : '#fff',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                          {!t.tutor_subject_id && <WarningOutlined style={{ color: '#fa8c16', fontSize: 12, flexShrink: 0 }} title="Not linked to a subject" />}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{t.stage_count} stages · {t.subject_display || t.tutor_subject_name || '—'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <Button size="small" type="text" icon={<EyeOutlined />}
                          onClick={e => { e.stopPropagation(); handleTogglePreview(t.template_id); }}
                          style={{ color: t.is_preview ? '#1677ff' : '#ccc' }}
                          title={t.is_preview ? 'In preview' : 'Not in preview'} />
                        <Popconfirm title="Delete this template?" onConfirm={e => { e.stopPropagation(); handleDeleteTemplate(t.template_id); }}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={e => e.stopPropagation()} />
                        </Popconfirm>
                      </div>
                    </div>
                  </List.Item>
                )} />
              )
          }
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, height: '100%', overflowY: 'auto' }}>
          {!selected
            ? <div style={{ textAlign: 'center', padding: 80, color: '#bbb' }}>
                <AppstoreOutlined style={{ fontSize: 40, marginBottom: 12 }} />
                <p style={{ fontSize: 14 }}>Select a template to edit its stages</p>
              </div>
            : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#0a0a0a' }}>{selected.title}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{stages.length} stages · drag to reorder</div>
                    </div>
                    <Button size="small" type="text" icon={<EditOutlined />} style={{ color: '#aaa', flexShrink: 0 }}
                      onClick={() => { renameForm.setFieldsValue({ title: selected.title, tutor_subject_id: selected.tutor_subject_id }); setRenameModal(true); }} />
                  </div>
                  <Button icon={<PlusOutlined />} type="primary" style={{ background: '#0a0a0a', border: 'none', flexShrink: 0 }} onClick={() => openStageModal()}>
                    Add stage
                  </Button>
                </div>

                {stagesLoading
                  ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                  : stages.length === 0
                    ? <Empty description="No stages yet. Add one above." imageStyle={{ height: 40 }} />
                    : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={stages.map(s => s.stage_id)} strategy={verticalListSortingStrategy}>
                          {stages.map(stage => (
                            <SortableStageCard key={stage.stage_id} stage={stage} onEdit={openStageModal} onDelete={handleDeleteStage} />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )
                }
              </>
            )
          }
        </div>
      </div>

      {/* ── New template modal (blank + MOE tabs) ── */}
      <Modal title="New template" open={newTmplModal} maskClosable={false}
        onCancel={() => { setNewTmplModal(false); tmplForm.resetFields(); moeForm.resetFields(); setSelectedMoe(null); }}
        footer={null} width={620}>
        <Tabs activeKey={newTmplTab} onChange={k => { setNewTmplTab(k); setSelectedMoe(null); }} style={{ marginTop: 8 }}
          items={[
            {
              key: 'blank',
              label: 'Start blank',
              children: (
                <Form form={tmplForm} layout="vertical" onFinish={handleCreateBlank} requiredMark={false}>
                  <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                    <Input placeholder="e.g. My E-Math Roadmap" />
                  </Form.Item>
                  <Form.Item label="Subject" name="tutor_subject_id" rules={[{ required: true, message: 'Please link to a subject' }]}>
                    <Select placeholder="Pick one of your subjects">
                      {subjects.map(s => <Select.Option key={s.subject_id} value={s.subject_id}>{s.subject_name} ({s.level})</Select.Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Optional description" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block loading={saving} style={{ background: '#0a0a0a', border: 'none' }}>
                    Create template
                  </Button>
                </Form>
              ),
            },
            {
              key: 'moe',
              label: 'Start from MOE syllabus',
              children: (
                <div>
                  {moeLoading
                    ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                    : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
                          {systemTmpls.map(t => (
                            <div key={t.template_id}
                              onClick={() => { setSelectedMoe(t); moeForm.setFieldsValue({ title: `${t.title} (My Copy)` }); }}
                              style={{
                                padding: '10px 14px', border: `1px solid ${selectedMoe?.template_id === t.template_id ? '#0a0a0a' : '#e8e8e8'}`,
                                borderRadius: 8, cursor: 'pointer',
                                background: selectedMoe?.template_id === t.template_id ? '#f5f5f5' : '#fff',
                              }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
                              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{t.stage_count} stages · {t.subject_level}</div>
                            </div>
                          ))}
                        </div>
                        {selectedMoe && (
                          <Form form={moeForm} layout="vertical" onFinish={handleCopyMoe} requiredMark={false}>
                            <Form.Item label="Title" name="title">
                              <Input />
                            </Form.Item>
                            <Form.Item label="Subject" name="tutor_subject_id" rules={[{ required: true, message: 'Please select a subject' }]}>
                              <Select placeholder="Pick one of your subjects">
                                {subjects.map(s => <Select.Option key={s.subject_id} value={s.subject_id}>{s.subject_name} ({s.level})</Select.Option>)}
                              </Select>
                            </Form.Item>
                            <Button type="primary" htmlType="submit" block style={{ background: '#0a0a0a', border: 'none' }}>
                              Copy template
                            </Button>
                          </Form>
                        )}
                      </>
                    )
                  }
                </div>
              ),
            },
          ]}
        />
      </Modal>

      {/* ── Stage add/edit modal ── */}
      <Modal title={editingStage ? 'Edit stage' : 'Add stage'} open={stageModal} maskClosable={false}
        onCancel={() => { setStageModal(false); stageForm.resetFields(); setEditingStage(null); setStageQuestions([]); setPreviewIdx(null); }}
        footer={null} width={560}>
        <Form form={stageForm} layout="vertical" onFinish={handleSaveStage} style={{ marginTop: 16 }} requiredMark={false}>
          <Form.Item label="Stage name" name="stage_name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Quadratic Equations" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Brief overview of this stage" />
          </Form.Item>
          <Form.Item label="Stage criteria" name="gate_description">
            <Input.TextArea autoSize={{ minRows: 3 }} placeholder="Describe what you will assess at this stage." />
          </Form.Item>

          {/* Questions */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                Questions <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span>
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <InputNumber
                  min={1} max={20} value={aiCount} onChange={v => setAiCount(v || 1)}
                  size="small" style={{ width: 60 }}
                  prefix={<span style={{ fontSize: 11, color: '#aaa' }}>#</span>}
                />
                <Button size="small" icon={<ThunderboltOutlined style={{ color: '#fa8c16' }} />}
                  loading={aiGenerating} onClick={handleGenerateQuestions}
                  style={{ borderColor: '#fa8c16', color: '#fa8c16' }}>
                  Generate with AI
                </Button>
              </div>
            </div>
            {stageQuestions.map((q, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {q.image_url ? (
                    <div style={{ flex: 1, border: '1px solid #e8e8e8', borderRadius: 6, padding: 8, background: '#fafafa' }}>
                      <a href={q.image_url} target="_blank" rel="noreferrer">
                        <img src={q.image_url} alt={`Question ${i + 1}`}
                          style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 4, objectFit: 'contain', display: 'block' }} />
                      </a>
                      <Input size="small" placeholder="Optional caption / instructions"
                        value={q.question_text}
                        onChange={e => setStageQuestions(prev => prev.map((x, xi) => xi === i ? { ...x, question_text: e.target.value } : x))}
                        style={{ marginTop: 8 }} />
                    </div>
                  ) : previewIdx === i ? (
                    <div style={{ flex: 1, minHeight: 60, padding: '8px 12px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 13, lineHeight: 1.7 }}>
                      <MathRenderer content={q.question_text} />
                    </div>
                  ) : (
                    <Input.TextArea autoSize={{ minRows: 2 }} value={q.question_text} placeholder={`Question ${i + 1}`}
                      onChange={e => setStageQuestions(prev => prev.map((x, xi) => xi === i ? { ...x, question_text: e.target.value } : x))}
                      style={{ flex: 1 }} />
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {!q.image_url && (
                      <Button size="small" type="text" style={{ color: '#888', fontSize: 11 }}
                        onClick={() => setPreviewIdx(previewIdx === i ? null : i)}>
                        {previewIdx === i ? 'Edit' : 'Preview'}
                      </Button>
                    )}
                    <Button type="text" danger size="small" icon={<DeleteOutlined />}
                      onClick={() => setStageQuestions(prev => prev.filter((_, xi) => xi !== i))} />
                  </div>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="dashed" icon={<PlusOutlined />} size="small"
                onClick={() => setStageQuestions(prev => [...prev, { question_text: '', image_url: null }])}>
                Type a question
              </Button>
              <Upload beforeUpload={handleUploadQuestionImage} showUploadList={false} accept="image/*">
                <Button type="dashed" icon={<PictureOutlined />} size="small" loading={imgUploading}>
                  Upload image
                </Button>
              </Upload>
            </div>
          </div>

          <Form.Item label="Difficulty" name="difficulty" initialValue="intermediate">
            <Radio.Group>
              <Radio.Button value="foundation" style={{ color: DIFF_COLOUR.foundation }}>Foundation</Radio.Button>
              <Radio.Button value="intermediate" style={{ color: DIFF_COLOUR.intermediate }}>Intermediate</Radio.Button>
              <Radio.Button value="extension" style={{ color: DIFF_COLOUR.extension }}>Extension</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Estimated effort (minutes)" name="effort_minutes" initialValue={30}>
            <InputNumber min={5} max={180} step={5} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={saving} style={{ background: '#0a0a0a', border: 'none' }}>
            {editingStage ? 'Save changes' : 'Add stage'}
          </Button>
        </Form>
      </Modal>

      {/* ── Rename/edit template modal ── */}
      <Modal title="Edit template" open={renameModal} maskClosable={false}
        onCancel={() => { setRenameModal(false); renameForm.resetFields(); }} footer={null}>
        <Form form={renameForm} layout="vertical" onFinish={handleRename} style={{ marginTop: 16 }} requiredMark={false}>
          <Form.Item name="title" label="Template name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tutor_subject_id" label="Subject" rules={[{ required: true, message: 'Please link to a subject' }]}>
            <Select placeholder="Pick one of your subjects">
              {subjects.map(s => <Select.Option key={s.subject_id} value={s.subject_id}>{s.subject_name} ({s.level})</Select.Option>)}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" block style={{ background: '#0a0a0a', border: 'none' }}>Save</Button>
        </Form>
      </Modal>
      </div>
    </AppLayout>
  );
}
